import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../../App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Clock, Users, ChevronDown, ChevronUp, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function PendingAttendanceWidget({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [sending, setSending] = useState(false);

  const today = new Date().getDay();
  // Only show on working days: Ahad(0) - Khamis(4)
  const isWorkingDay = today >= 0 && today <= 4;

  const fetchPending = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/daily-logs/pending-attendance");
      setData(res.data);

      const sessionKey = `attendance_popup_dismissed_${res.data.date}`;
      const alreadyDismissed = sessionStorage.getItem(sessionKey);
      // Show popup if before deadline, has pending, and not yet dismissed this session
      if (
        res.data.is_before_deadline &&
        res.data.pending_count > 0 &&
        !alreadyDismissed &&
        !popupDismissed
      ) {
        setShowPopup(true);
      }
    } catch (err) {
      console.error("Error fetching pending attendance:", err);
    } finally {
      setLoading(false);
    }
  }, [popupDismissed]);

  useEffect(() => {
    if (!isWorkingDay) return; // Jangan fetch pada Jumaat/Sabtu
    fetchPending();
    // Refresh every 2 minutes
    const interval = setInterval(fetchPending, 120000);
    return () => clearInterval(interval);
  }, [fetchPending, isWorkingDay]);

  const dismissPopup = () => {
    setShowPopup(false);
    setPopupDismissed(true);
    if (data?.date) {
      sessionStorage.setItem(`attendance_popup_dismissed_${data.date}`, "true");
    }
  };

  const handleNotifySuperiors = async () => {
    setSending(true);
    try {
      await axiosInstance.post("/daily-logs/notify-attendance-summary");
      toast.success("Notifikasi ringkasan kehadiran telah dihantar kepada penyelia!");
      setSending(false);
    } catch (err) {
      toast.error("Gagal menghantar notifikasi");
      setSending(false);
    }
  };

  const goToDailyLogs = () => {
    dismissPopup();
    navigate("/daily-logs");
  };

  if (!isWorkingDay) return null; // Hanya Ahad-Khamis
  if (loading || !data) return null;
  if (data.total_children === 0) return null;

  const { pending_count, logged_children, total_children, is_before_deadline, pending_children, current_time } = data;
  const progress = total_children > 0 ? Math.round((logged_children / total_children) * 100) : 0;
  const allDone = pending_count === 0;

  return (
    <>
      <div
        data-testid="pending-attendance-widget"
        className={`mb-6 rounded-2xl shadow-lg border-2 overflow-hidden transition-all duration-300 ${
          allDone
            ? "bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200"
            : is_before_deadline
            ? "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 animate-pulse-slow"
            : "bg-gradient-to-r from-red-50 to-rose-50 border-red-300"
        }`}
      >
        <div
          className={`px-4 py-3 flex items-center justify-between cursor-pointer ${
            allDone
              ? "bg-gradient-to-r from-emerald-500 to-green-500"
              : is_before_deadline
              ? "bg-gradient-to-r from-amber-500 to-orange-500"
              : "bg-gradient-to-r from-red-500 to-rose-500"
          }`}
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              {allDone ? (
                <CheckCircle2 className="w-6 h-6 text-white" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">
                {allDone ? "Log Harian Lengkap!" : "Log Harian Belum Lengkap"}
              </h3>
              <p className="text-white/80 text-xs">
                {allDone
                  ? `Semua ${total_children} kanak-kanak telah direkod`
                  : `${pending_count} daripada ${total_children} belum diisi`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!allDone && is_before_deadline && (
              <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-lg backdrop-blur-sm">
                <Clock className="w-3 h-3 text-white" />
                <span className="text-white text-xs font-bold">Sblm 3:00 PTG</span>
              </div>
            )}
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-white" />
            ) : (
              <ChevronDown className="w-5 h-5 text-white" />
            )}
          </div>
        </div>

        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-bold text-gray-600">Kemajuan</span>
            <span className={`font-black ${allDone ? "text-emerald-600" : "text-amber-600"}`}>
              {logged_children}/{total_children} ({progress}%)
            </span>
          </div>
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                allDone
                  ? "bg-gradient-to-r from-emerald-400 to-green-500"
                  : progress > 50
                  ? "bg-gradient-to-r from-amber-400 to-yellow-500"
                  : "bg-gradient-to-r from-red-400 to-orange-500"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {expanded && (
          <div className="px-4 pb-4">
            {pending_count > 0 && (
              <>
                <p className="text-xs font-bold text-gray-500 mt-2 mb-2">
                  Kanak-kanak belum diisi log harian:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {pending_children.map((child) => (
                    <div
                      key={child.id}
                      className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-amber-200 shadow-sm"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {child.name?.charAt(0) || "?"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-gray-800 truncate">{child.name}</p>
                        <p className="text-xs text-gray-500 truncate">{child.class_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              {pending_count > 0 && (
                <Button
                  data-testid="go-to-daily-logs-btn"
                  onClick={goToDailyLogs}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl font-bold text-sm shadow-lg"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Isi Log Harian Sekarang
                </Button>
              )}
              {logged_children > 0 && (
                <Button
                  data-testid="notify-superiors-btn"
                  onClick={handleNotifySuperiors}
                  disabled={sending}
                  variant="outline"
                  className="flex-1 border-2 border-purple-300 text-purple-700 hover:bg-purple-50 rounded-xl font-bold text-sm"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? "Menghantar..." : "Hantar Notifikasi Kehadiran"}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog open={showPopup} onOpenChange={(open) => { if (!open) dismissPopup(); }}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl" data-testid="attendance-popup-modal">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 text-center relative">
            <button
              onClick={dismissPopup}
              className="absolute top-3 right-3 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            <div className="w-16 h-16 mx-auto bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm mb-3">
              <AlertTriangle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-white font-black text-lg">Peringatan Kehadiran</h2>
            <p className="text-white/80 text-sm mt-1">
              Sila sahkan log harian sebelum 3:00 PTG
            </p>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-red-50 rounded-xl border border-red-200">
                <p className="text-2xl font-black text-red-600">{pending_count}</p>
                <p className="text-xs font-bold text-red-500">Belum Diisi</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-xl border border-green-200">
                <p className="text-2xl font-black text-green-600">{logged_children}</p>
                <p className="text-xs font-bold text-green-500">Sudah Diisi</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-2xl font-black text-blue-600">{total_children}</p>
                <p className="text-xs font-bold text-blue-500">Jumlah</p>
              </div>
            </div>

            {pending_children && pending_children.length > 0 && (
              <div>
                <p className="text-xs font-bold text-gray-500 mb-2">
                  Kanak-kanak yang belum dilog:
                </p>
                <div className="max-h-36 overflow-y-auto space-y-1.5">
                  {pending_children.slice(0, 8).map((child) => (
                    <div
                      key={child.id}
                      className="flex items-center gap-2 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200"
                    >
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {child.name?.charAt(0) || "?"}
                      </div>
                      <p className="text-xs font-semibold text-gray-700 truncate">{child.name}</p>
                      <span className="text-xs text-gray-400 ml-auto truncate">{child.class_name?.split("(")[0]?.trim()}</span>
                    </div>
                  ))}
                  {pending_children.length > 8 && (
                    <p className="text-xs text-gray-400 text-center">
                      ...dan {pending_children.length - 8} lagi
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Button
                data-testid="popup-go-daily-logs-btn"
                onClick={goToDailyLogs}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold rounded-xl py-3 shadow-lg"
              >
                Sahkan Kehadiran Sekarang
              </Button>
              <Button
                variant="ghost"
                onClick={dismissPopup}
                className="w-full text-gray-500 hover:text-gray-700 font-medium text-sm"
              >
                Ingatkan Saya Nanti
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.95; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}
